/* =====================================================================
 * Checkbox — 공통 체크박스 컴포넌트 (테마 기반)
 * ===================================================================== */

import React from 'react';
import styled, { css } from 'styled-components';

type CheckboxSize = 'sm' | 'md';

interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  size?: CheckboxSize;
  disabled?: boolean;
  label?: React.ReactNode;
  className?: string;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  onChange,
  size = 'md',
  disabled = false,
  label,
  className,
}) => {
  const handleWrapperClick = (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    onChange(!checked);
  };

  return (
    <Wrapper
      className={className}
      $disabled={disabled}
      onClick={handleWrapperClick}
    >
      <Box $checked={checked} $size={size} $disabled={disabled}>
        {checked && (
          <CheckMark $size={size} viewBox="0 0 12 12" fill="none">
            <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </CheckMark>
        )}
      </Box>
      <HiddenInput
        type="checkbox"
        $size={size}
        checked={checked}
        onChange={(e) => {
          if (!disabled) {
            onChange(e.target.checked);
          }
        }}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
      />
      {label && <Label $disabled={disabled}>{label}</Label>}
    </Wrapper>
  );
};

const sizeMap = { sm: 16, md: 18 };

const Wrapper = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  position: relative;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
`;

const Box = styled.div<{ $checked: boolean; $size: CheckboxSize; $disabled: boolean }>`
  width: ${({ $size }) => sizeMap[$size]}px;
  height: ${({ $size }) => sizeMap[$size]}px;
  border-radius: 4px;
  border: 2px solid ${({ $checked, theme }) => ($checked ? theme.colors.accent : theme.colors.border)};
  background: ${({ $checked, theme }) => ($checked ? theme.colors.accent : 'transparent')};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${({ theme }) => theme.transitions.fast};
  color: ${({ theme }) => theme.colors.textInverse};

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
    `}
`;

const CheckMark = styled.svg<{ $size: CheckboxSize }>`
  width: ${({ $size }) => sizeMap[$size] - 4}px;
  height: ${({ $size }) => sizeMap[$size] - 4}px;
`;

const HiddenInput = styled.input<{ $size: CheckboxSize }>`
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  width: ${({ $size }) => sizeMap[$size]}px;
  height: ${({ $size }) => sizeMap[$size]}px;
  margin: 0;
`;

const Label = styled.span<{ $disabled: boolean }>`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.5;
    `}
`;
